import { StripeService } from './stripeService';

export interface PlanLimits {
  maxRooms: number;
  maxParticipantsPerRoom: number;
  maxBroadcastLinksPerRoom: number;
  maxApiKeys: number;
  canUseCustomBranding: boolean;
  canUseAnalytics: boolean;
  canUseRecording: boolean;
  canUseAdvancedSecurity: boolean;
  canUseSSO: boolean;
  canUseCustomDomain: boolean;
  supportLevel: 'email' | 'priority' | 'dedicated';
}

export const PLAN_LIMITS: Record<string, PlanLimits> = {
  free: {
    maxRooms: 10,
    maxParticipantsPerRoom: 10,
    maxBroadcastLinksPerRoom: 1,
    maxApiKeys: 2,
    canUseCustomBranding: false,
    canUseAnalytics: false,
    canUseRecording: false,
    canUseAdvancedSecurity: false,
    canUseSSO: false,
    canUseCustomDomain: false,
    supportLevel: 'email',
  },
  pro: {
    maxRooms: -1, // Unlimited rooms
    maxParticipantsPerRoom: 50,
    maxBroadcastLinksPerRoom: 5,
    maxApiKeys: 10,
    canUseCustomBranding: true,
    canUseAnalytics: true,
    canUseRecording: true,
    canUseAdvancedSecurity: false,
    canUseSSO: false,
    canUseCustomDomain: false,
    supportLevel: 'priority',
  },
  enterprise: {
    maxRooms: -1, // unlimited
    maxParticipantsPerRoom: 200,
    maxBroadcastLinksPerRoom: -1, // unlimited
    maxApiKeys: -1, // unlimited
    canUseCustomBranding: true,
    canUseAnalytics: true,
    canUseRecording: true,
    canUseAdvancedSecurity: true,
    canUseSSO: true,
    canUseCustomDomain: true,
    supportLevel: 'dedicated',
  },
};

export class SubscriptionService {
  /**
   * Get current user's plan
   */
  static async getCurrentPlan(): Promise<string> {
    try {
      const subscription = await StripeService.getUserSubscription();

      if (!subscription?.price_id) {
        return 'free';
      }

      const product = StripeService.getProductByPriceId(subscription.price_id);

      if (product?.name === 'Pro Plan') {
        return 'pro';
      } else if (product?.name === 'Enterprise Plan') {
        return 'enterprise';
      }

      return 'free';
    } catch (error) {
      console.error('Failed to get current plan:', error);
      return 'free';
    }
  }

  /**
   * Get plan limits for current user
   */
  static async getCurrentPlanLimits(): Promise<PlanLimits> {
    const plan = await this.getCurrentPlan();
    return PLAN_LIMITS[plan];
  }

  /**
   * Check if user can create a new room
   */
  static async canCreateRoom(
    currentRoomCount: number
  ): Promise<{ allowed: boolean; reason?: string }> {
    const limits = await this.getCurrentPlanLimits();

    if (limits.maxRooms === -1) {
      return { allowed: true };
    }

    if (currentRoomCount >= limits.maxRooms) {
      return {
        allowed: false,
        reason: `You've reached the maximum of ${limits.maxRooms} rooms for your plan. Upgrade to create more rooms.`,
      };
    }

    return { allowed: true };
  }

  /**
   * Check if user can add more participants to a room
   */
  static async canAddParticipant(
    currentParticipantCount: number
  ): Promise<{ allowed: boolean; reason?: string }> {
    const limits = await this.getCurrentPlanLimits();

    if (currentParticipantCount >= limits.maxParticipantsPerRoom) {
      return {
        allowed: false,
        reason: `You've reached the maximum of ${limits.maxParticipantsPerRoom} participants per room for your plan. Upgrade to add more participants.`,
      };
    }

    return { allowed: true };
  }

  /**
   * Check if user can create more broadcast links
   */
  static async canCreateBroadcastLink(
    currentLinkCount: number
  ): Promise<{ allowed: boolean; reason?: string }> {
    const limits = await this.getCurrentPlanLimits();

    if (limits.maxBroadcastLinksPerRoom === -1) {
      return { allowed: true };
    }

    if (currentLinkCount >= limits.maxBroadcastLinksPerRoom) {
      return {
        allowed: false,
        reason: `You've reached the maximum of ${limits.maxBroadcastLinksPerRoom} broadcast links per room for your plan. Upgrade to create more links.`,
      };
    }

    return { allowed: true };
  }

  /**
   * Check if user can create more API keys
   */
  static async canCreateApiKey(
    currentApiKeyCount: number
  ): Promise<{ allowed: boolean; reason?: string }> {
    const limits = await this.getCurrentPlanLimits();

    if (limits.maxApiKeys === -1) {
      return { allowed: true };
    }

    if (currentApiKeyCount >= limits.maxApiKeys) {
      return {
        allowed: false,
        reason: `You've reached the maximum of ${limits.maxApiKeys} API keys for your plan. Upgrade to create more API keys.`,
      };
    }

    return { allowed: true };
  }

  /**
   * Check if user can access a feature
   */
  static async canAccessFeature(
    feature: keyof PlanLimits
  ): Promise<{ allowed: boolean; reason?: string }> {
    const limits = await this.getCurrentPlanLimits();

    if (typeof limits[feature] === 'boolean' && !limits[feature]) {
      return {
        allowed: false,
        reason: `This feature is not available in your current plan. Upgrade to access ${feature
          .replace(/([A-Z])/g, ' $1')
          .toLowerCase()}.`,
      };
    }

    return { allowed: true };
  }

  /**
   * Get upgrade suggestions based on current plan
   */
  static async getUpgradeSuggestions(): Promise<string[]> {
    const plan = await this.getCurrentPlan();

    switch (plan) {
      case 'free':
        return [
          'Upgrade to Pro for unlimited rooms',
          'Get advanced customization options',
          'Access analytics dashboard and custom branding',
          'Enjoy priority support',
        ];
      case 'pro':
        return [
          'Upgrade to Enterprise for dedicated account manager',
          'Support up to 200 participants per room',
          'Access advanced security features and API',
          'Get custom integration support',
        ];
      default:
        return [];
    }
  }

  /**
   * Get plan display name
   */
  static getPlanDisplayName(plan: string): string {
    switch (plan) {
      case 'free':
        return 'Free Plan';
      case 'pro':
        return 'Pro Plan';
      case 'enterprise':
        return 'Enterprise Plan';
      default:
        return 'Unknown Plan';
    }
  }
}